import React, { useCallback, useMemo } from 'react'

import {
  Box,
  Checkbox,
  Flex,
  Input,
  Spacer,
  Stack,
  Text,
} from '@chakra-ui/react'
import { useDispatch, useSelector } from 'react-redux'

import { setCaseworkFilters } from '../../../redux/features/caseworkSlice'
import { setDirectoryFilters } from '../../../redux/features/directorySlice'
import {
  NoSearchResults,
  SearchOption,
  SearchOptions,
  SearchSelect,
  SearchSelectedTags,
} from '../select'

const FilterInput = ({
  options,
  label,
  placeholder,
  filterKey,
  store,
  inputType = 'text',
  formatOption,
  labelNode,
  ...props
}) => {
  const dispatch = useDispatch()

  const filters = useSelector(state => state[store].filters)

  const selected = filters[filterKey] || []

  const updateFilters = useCallback(
    filter => {
      let filterObj = { ...filters, [filterKey]: filter }

      if (store === 'directory') dispatch(setDirectoryFilters(filterObj))
      if (store === 'casework') dispatch(setCaseworkFilters(filterObj))
    },
    [store, dispatch, filters, filterKey]
  )

  const clearFilters = useCallback(
    () => updateFilters(undefined),
    [updateFilters]
  )

  const handleOptionSelect = useCallback(
    selectedValue => {
      let updatedFilters = ['checkbox', 'date'].includes(inputType)
        ? []
        : [...selected]
      if (selectedValue && !selected.includes(selectedValue)) {
        updatedFilters.push(selectedValue)
      }

      updateFilters(updatedFilters)
    },
    [updateFilters, selected, inputType]
  )

  const handleOptionRemove = useCallback(
    removeIndex => {
      updateFilters(selected.toSpliced(removeIndex, 1))
    },
    [updateFilters, selected]
  )

  const handleEnter = useCallback(
    (e, { searchTerm, handleSelectOption }) => {
      if (e.key === 'Enter') {
        if (options) {
          let foundOption = options.find(
            o => o.toLowerCase() === searchTerm.toLowerCase()
          )
          if (foundOption) {
            // context select
            handleSelectOption({
              label: formatOption ? formatOption(foundOption) : foundOption,
              value: foundOption,
            })
            // filter input select
            handleOptionSelect(foundOption)
          }
        } else {
          handleSelectOption({
            label: formatOption ? formatOption(searchTerm) : searchTerm,
            value: searchTerm,
          })
          handleOptionSelect(searchTerm)
        }
      }
    },
    [handleOptionSelect, options, formatOption]
  )

  const filterOptions = useCallback(
    (searchTerm, selectedOptions) => {
      let filteredOptions = options.filter(option => {
        const optionValue = typeof option === 'string' ? option : option.value
        return !selectedOptions.find(({ value }) => value === optionValue)
      })

      if (searchTerm)
        filteredOptions = filteredOptions.filter(option => {
          const optionValue = typeof option === 'string' ? option : option.value
          const lowerOption = optionValue.toLowerCase()
          if (filterKey === 'district__in')
            return lowerOption.startsWith(searchTerm.toLowerCase())
          else return lowerOption.includes(searchTerm.toLowerCase())
        })

      if (filteredOptions.length === 0) return null
      return filteredOptions
    },
    [options, filterKey]
  )

  const selectedFiltersToOptions = useMemo(
    () =>
      selected?.map(option => ({
        label: formatOption ? formatOption(option) : option,
        value: option,
      })) || [],
    [selected, formatOption]
  )

  const handleDateChange = e => {
    handleOptionSelect(e.target.value)
  }
  return (
    <Box>
      <Flex>
        <Text fontSize='lg'> {label} </Text>
        <Spacer />
        {labelNode}
      </Flex>

      {inputType === 'checkbox' ? (
        <Stack direction='row' align='center'>
          <Checkbox
            id='filter-check'
            onChange={e => handleOptionSelect(e.target.checked)}
            isChecked={selected[0] === true}
          />
          <Text> {placeholder} </Text>
        </Stack>
      ) : inputType === 'date' ? (
        <Input
          type='date'
          onChange={handleDateChange}
          value={selected.length > 0 ? selected[0] : ''}
        />
      ) : (
        <SearchSelect>
          <SearchOptions
            onKeyDown={handleEnter}
            onClear={clearFilters}
            initialSelected={selectedFiltersToOptions}
            hideOptions={!options}
            inputProps={{
              id: `filter-input-${label?.toLowerCase()}`,
              placeholder: placeholder || 'Type or select option below...',
              type: inputType,
            }}
            {...props}
          >
            {({ searchTerm, selectedOptions }) =>
              filterOptions(searchTerm, selectedOptions)?.map(
                (option, index) => {
                  const isObjectOption = typeof option === 'object'
                  const optionValue = isObjectOption ? option.value : option
                  const optionLabel = isObjectOption
                    ? option.label
                    : formatOption
                    ? formatOption(option)
                    : option
                  const isInactive = isObjectOption && option.isInactive

                  return (
                    <SearchOption
                      key={index}
                      option={{
                        label: optionLabel,
                        value: optionValue,
                      }}
                      onSelect={handleOptionSelect}
                      color={isInactive ? 'gray.400' : undefined}
                      fontStyle={isInactive ? 'italic' : undefined}
                    >
                      {optionLabel}
                      {isInactive && ' (inactive)'}
                    </SearchOption>
                  )
                }
              ) || <NoSearchResults> No Results </NoSearchResults>
            }
          </SearchOptions>
          {inputType === 'text' && (
            <SearchSelectedTags onRemove={handleOptionRemove} />
          )}
        </SearchSelect>
      )}
    </Box>
  )
}

export default FilterInput
